// Reconciliadores de feature (SPEC-11) — efectos OPT-IN ante cambios de resolución.
//
// Problema: la mayoría de las features del rollout solo muestran/ocultan UI por variante
// (declarativo puro). Algunas necesitan EJECUTAR procesos cuando la resolución cambia
// (impresion-ble: resetear el estado BLE del equipo cuando el comercio regresó a control).
// Un sistema de "eventos de transición" se pierde transiciones por diseño: el cambio ocurre
// SERVER-SIDE (el root revierte en SU equipo) y este dispositivo se entera cuando re-resuelve
// — horas después, o nunca si estaba cerrado durante la transición.
//
// Por eso el contrato es de RECONCILIACIÓN (level-triggered), no de eventos (edge-triggered):
//   - El reconciliador es IDEMPOTENTE: compara el estado local contra la resolución vigente y
//     converge (correr 1 o N veces da lo mismo). La "transición" solo lo dispara pronto; la
//     hidratación de cada sesión es la red de seguridad que garantiza convergencia.
//   - Detección SÍNCRONA (en los call-sites que escriben resolución), ejecución ASÍNCRONA
//     DIFERIDA (queueMicrotask): nunca dentro del notify del store (reentrancia) ni en el
//     camino switch→render→outbox. Un reconciliador que LANZA no rompe nada (try/catch).
//   - `cause` distingue la intención y evita falsos disparos: un 'optimistic_rollback'
//     (rebote REQUIRES_ROOT que revierte un optimista) NO es decisión del comercio; un
//     'switch' es el optimista local; solo 'hydration' es la resolución AUTORITATIVA.
//
// Features show/hide: no registran nada → costo cero, comportamiento idéntico.

import type { FeatureVersionCacheEntry } from './types';

export type ReconcileCause = 'hydration' | 'switch' | 'optimistic_rollback' | 'session_reset';

export interface ReconcileContext {
  /** Resolución vigente (la entrada recién escrita/confirmada). */
  entry: FeatureVersionCacheEntry;
  /** Entrada anterior, si el disparo vino de una transición viva en este dispositivo. */
  prev?: FeatureVersionCacheEntry;
  cause: ReconcileCause;
}

type Reconciler = (ctx: ReconcileContext) => void;

const reconcilers = new Map<string, Set<Reconciler>>();

// Registra el reconciliador de una feature. Llamar a nivel MÓDULO del hook de la feature
// (p.ej. useBlePrintingRollout.ts): así queda vivo en cualquier página donde el gate se
// monte (navbar lo monta globalmente), no solo en Ajustes. Devuelve el des-registro.
export function registerFeatureReconciler(featureKey: string, reconcile: Reconciler): () => void {
  let set = reconcilers.get(featureKey);
  if (!set) {
    set = new Set();
    reconcilers.set(featureKey, set);
  }
  set.add(reconcile);
  return () => {
    set?.delete(reconcile);
  };
}

// Agenda la reconciliación de una feature (diferida a microtask). La invocan los call-sites
// que escriben resolución (hidratación autoritativa, optimistas, rollback del outbox). Es
// fire-and-forget: un reconciliador roto se reporta best-effort y NO afecta al resto ni al
// flujo que lo disparó.
export function scheduleReconcile(featureKey: string, ctx: ReconcileContext): void {
  const set = reconcilers.get(featureKey);
  if (!set || set.size === 0) return;
  const toRun = Array.from(set);
  queueMicrotask(() => {
    toRun.forEach((reconcile) => {
      try {
        reconcile(ctx);
      } catch {
        // Idempotente y best-effort: la próxima reconciliación (hidratación) converge.
      }
    });
  });
}

// Solo para tests.
export function __resetReconcilersForTests(): void {
  reconcilers.clear();
}
